/**
 * Canvas 2D renderer for a `source_plan_projection_chunk`.
 *
 * v1 intentionally keeps this simple: fit the storey bbox to the canvas, draw
 * each primitive as a polyline, colour by IFC class. Pan/zoom are deferred to a
 * future iteration; the first goal is to prove the projection surface is
 * consumable by a second viewer without touching the 3D renderer.
 */

import type { AxisLineEntry, PlanProjectionChunk, PlanProjectionPrimitive } from "./types";

const AXIS_STROKE = "#f6c85f";
const AXIS_SELECTED_STROKE = "#ffffff";
const AXIS_HIT_RADIUS_PX = 8;

export function axisLineTooltip(entry: AxisLineEntry): string {
  const length = entry.axis_length_mm.toLocaleString("en-US", { maximumFractionDigits: 3 });
  return `${entry.ifc_class} #${entry.identity.express_id} · Axis length ${length} mm`;
}

export function distanceToSegment(
  point: readonly [number, number],
  start: readonly [number, number],
  end: readonly [number, number],
): number {
  const dx = end[0] - start[0];
  const dy = end[1] - start[1];
  if (dx === 0 && dy === 0) {
    return Math.hypot(point[0] - start[0], point[1] - start[1]);
  }
  const t = Math.max(
    0,
    Math.min(1, ((point[0] - start[0]) * dx + (point[1] - start[1]) * dy) / (dx * dx + dy * dy)),
  );
  return Math.hypot(point[0] - (start[0] + t * dx), point[1] - (start[1] + t * dy));
}

const CLASS_PALETTE = [
  "#6fb2ff",
  "#ff8a80",
  "#69f0ae",
  "#ffd180",
  "#e6ee9c",
  "#b39ddb",
  "#80deea",
  "#ffab91",
  "#ce93d8",
  "#a5d6a7",
];

function classColor(className: string): string {
  let hash = 0;
  for (let i = 0; i < className.length; i += 1) {
    hash = (hash * 31 + className.charCodeAt(i)) | 0;
  }
  return CLASS_PALETTE[Math.abs(hash) % CLASS_PALETTE.length];
}

function decodePoints(
  blob: Uint8Array,
  byteOffset: number,
  pointCount: number,
  originMm: readonly [number, number],
): Array<[number, number]> {
  const slice = blob.slice(byteOffset, byteOffset + pointCount * 8);
  const view = new DataView(slice.buffer, slice.byteOffset, slice.byteLength);
  const points: Array<[number, number]> = [];
  for (let i = 0; i < pointCount; i += 1) {
    const dx = view.getInt32(i * 8, true);
    const dy = view.getInt32(i * 8 + 4, true);
    points.push([dx + originMm[0], dy + originMm[1]]);
  }
  return points;
}

function styleForPrimitive(primitive: PlanProjectionPrimitive, baseColor: string): {
  stroke: string;
  fill: string | null;
  lineWidth: number;
} {
  // Grids are reference lines, not building geometry.
  if (primitive.layer === "grid" || primitive.kind === "grid") {
    return {
      stroke: "#93a0b1",
      fill: null,
      lineWidth: 0.75,
    };
  }
  // MEP centerlines are thin and distinct.
  if (primitive.layer === "mep" || primitive.kind === "centerline") {
    return {
      stroke: baseColor,
      fill: null,
      lineWidth: 1,
    };
  }
  // Closed architectural cut outlines are the main plan polygons.
  if (primitive.kind === "cut" && primitive.topology === "closed_polyline") {
    return {
      stroke: baseColor,
      fill: `${baseColor}26`, // 15% opacity hex alpha
      lineWidth: 1.5,
    };
  }
  return {
    stroke: baseColor,
    fill: null,
    lineWidth: 1.5,
  };
}

export class PlanViewer {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private lastChunk: PlanProjectionChunk | null = null;
  private axisLines: readonly AxisLineEntry[] = [];
  private selectedAxisExpressId: number | null = null;
  private readonly onAxisSelect: ((entry: AxisLineEntry) => void) | null;
  private readonly tooltip: HTMLDivElement;
  private axisHitSegments: Array<{
    readonly entry: AxisLineEntry;
    readonly start: readonly [number, number];
    readonly end: readonly [number, number];
  }> = [];

  constructor(
    canvas: HTMLCanvasElement,
    options: { readonly onAxisSelect?: (entry: AxisLineEntry) => void } = {},
  ) {
    this.canvas = canvas;
    const ctx = canvas.getContext("2d");
    if (ctx === null) {
      throw new Error("Canvas 2D context not available");
    }
    this.ctx = ctx;
    this.onAxisSelect = options.onAxisSelect ?? null;
    this.tooltip = document.createElement("div");
    this.tooltip.className = "plan-axis-tooltip";
    this.tooltip.hidden = true;
    canvas.parentElement?.append(this.tooltip);
    canvas.tabIndex = 0;
    canvas.setAttribute("role", "img");
    canvas.setAttribute(
      "aria-label",
      "2D source plan. Dashed Axis-only lines can be selected with pointer input.",
    );
    canvas.addEventListener("pointermove", (event) => this.handlePointerMove(event));
    canvas.addEventListener("pointerleave", () => this.hideTooltip());
    canvas.addEventListener("click", (event) => this.handleClick(event));
  }

  /** Redraw the last-rendered chunk at the current canvas size. */
  redraw(): void {
    if (this.lastChunk !== null) {
      this.render(this.lastChunk);
    }
  }

  clear(): void {
    this.lastChunk = null;
    this.axisLines = [];
    this.selectedAxisExpressId = null;
    this.axisHitSegments = [];
    this.hideTooltip();
    this.clearCanvas();
  }

  setAxisLines(lines: readonly AxisLineEntry[], selectedExpressId: number | null): void {
    this.axisLines = lines;
    this.selectedAxisExpressId = selectedExpressId;
    this.redraw();
  }

  selectAxisLine(expressId: number | null): void {
    this.selectedAxisExpressId = expressId;
    this.redraw();
  }

  private clearCanvas(): void {
    const { canvas, ctx } = this;
    const dpr = window.devicePixelRatio || 1;
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    canvas.width = Math.floor(width * dpr);
    canvas.height = Math.floor(height * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);
  }

  render(chunk: PlanProjectionChunk): void {
    this.lastChunk = chunk;
    this.clearCanvas();
    this.axisHitSegments = [];
    const { ctx } = this;
    const width = this.canvas.clientWidth;
    const height = this.canvas.clientHeight;

    const axisPoints = this.axisLines.flatMap((entry) => entry.polylines_mm.flat());
    const sourceBbox = chunk.header.bboxMm;
    const bbox: [number, number, number, number] = [...sourceBbox];
    for (const point of axisPoints) {
      bbox[0] = Math.min(bbox[0], point[0]);
      bbox[1] = Math.min(bbox[1], point[1]);
      bbox[2] = Math.max(bbox[2], point[0]);
      bbox[3] = Math.max(bbox[3], point[1]);
    }
    if (axisPoints.length > 0 && chunk.header.primitives.length === 0) {
      bbox[0] = Math.min(...axisPoints.map((point) => point[0]));
      bbox[1] = Math.min(...axisPoints.map((point) => point[1]));
      bbox[2] = Math.max(...axisPoints.map((point) => point[0]));
      bbox[3] = Math.max(...axisPoints.map((point) => point[1]));
    }
    const span = Math.max(bbox[2] - bbox[0], bbox[3] - bbox[1], 1);
    if (bbox[2] - bbox[0] < 1) {
      bbox[0] -= span * 0.05;
      bbox[2] += span * 0.05;
    }
    if (bbox[3] - bbox[1] < 1) {
      bbox[1] -= span * 0.05;
      bbox[3] += span * 0.05;
    }
    const bboxW = bbox[2] - bbox[0];
    const bboxH = bbox[3] - bbox[1];
    if (bboxW <= 0 || bboxH <= 0 || width === 0 || height === 0) {
      return;
    }

    const padding = 24;
    const availableW = Math.max(1, width - padding * 2);
    const availableH = Math.max(1, height - padding * 2);
    const scale = Math.min(availableW / bboxW, availableH / bboxH);
    const offsetX = (width - bboxW * scale) / 2 - bbox[0] * scale;
    // Canvas Y grows downward while IFC world Y grows upward, so flip Y.
    const offsetY = (height + bboxH * scale) / 2 + bbox[1] * scale;

    for (const primitive of chunk.header.primitives) {
      const points = decodePoints(
        chunk.blob,
        primitive.byteOffset,
        primitive.pointCount,
        chunk.header.originMm,
      );
      if (points.length < 2) {
        continue;
      }

      const color = classColor(primitive.source.ifcClass);
      const style = styleForPrimitive(primitive, color);

      ctx.beginPath();
      const first = points[0];
      ctx.moveTo(first[0] * scale + offsetX, offsetY - first[1] * scale);
      for (let i = 1; i < points.length; i += 1) {
        const p = points[i];
        ctx.lineTo(p[0] * scale + offsetX, offsetY - p[1] * scale);
      }
      if (primitive.topology === "closed_polyline") {
        ctx.closePath();
      }

      if (style.fill !== null) {
        ctx.fillStyle = style.fill;
        ctx.fill();
      }
      ctx.strokeStyle = style.stroke;
      ctx.lineWidth = style.lineWidth;
      ctx.stroke();
    }

    for (const entry of this.axisLines) {
      const selected = entry.identity.express_id === this.selectedAxisExpressId;
      for (const line of entry.polylines_mm) {
        if (line.length < 2) {
          continue;
        }
        const screenPoints = line.map(
          (point) => [point[0] * scale + offsetX, offsetY - point[1] * scale] as const,
        );
        if (selected) {
          ctx.beginPath();
          ctx.moveTo(screenPoints[0][0], screenPoints[0][1]);
          for (const point of screenPoints.slice(1)) ctx.lineTo(point[0], point[1]);
          ctx.setLineDash([]);
          ctx.strokeStyle = "rgba(111, 178, 255, 0.75)";
          ctx.lineWidth = 7;
          ctx.stroke();
        }
        ctx.beginPath();
        ctx.moveTo(screenPoints[0][0], screenPoints[0][1]);
        for (const point of screenPoints.slice(1)) ctx.lineTo(point[0], point[1]);
        ctx.setLineDash([8, 5]);
        ctx.strokeStyle = selected ? AXIS_SELECTED_STROKE : AXIS_STROKE;
        ctx.lineWidth = selected ? 3 : 2;
        ctx.stroke();
        ctx.setLineDash([]);
        for (let i = 1; i < screenPoints.length; i += 1) {
          this.axisHitSegments.push({
            entry,
            start: screenPoints[i - 1],
            end: screenPoints[i],
          });
        }
      }
    }
  }

  private hitAxis(event: PointerEvent | MouseEvent): AxisLineEntry | null {
    const rect = this.canvas.getBoundingClientRect();
    const point: [number, number] = [event.clientX - rect.left, event.clientY - rect.top];
    let best: { entry: AxisLineEntry; distance: number } | null = null;
    for (const segment of this.axisHitSegments) {
      const distance = distanceToSegment(point, segment.start, segment.end);
      if (distance <= AXIS_HIT_RADIUS_PX && (best === null || distance < best.distance)) {
        best = { entry: segment.entry, distance };
      }
    }
    return best?.entry ?? null;
  }

  private handlePointerMove(event: PointerEvent): void {
    const entry = this.hitAxis(event);
    if (entry === null) {
      this.hideTooltip();
      this.canvas.style.cursor = "default";
      return;
    }
    const rect = this.canvas.getBoundingClientRect();
    this.tooltip.textContent = axisLineTooltip(entry);
    this.tooltip.style.left = `${event.clientX - rect.left + 12}px`;
    this.tooltip.style.top = `${event.clientY - rect.top + 12}px`;
    this.tooltip.hidden = false;
    this.canvas.style.cursor = "pointer";
  }

  private handleClick(event: MouseEvent): void {
    const entry = this.hitAxis(event);
    if (entry !== null) {
      this.onAxisSelect?.(entry);
    }
  }

  private hideTooltip(): void {
    this.tooltip.hidden = true;
  }
}
